import { useEffect, useMemo, useState } from 'react'
import { Eye, MoreVertical, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useDeleteCompany } from '../../hooks/api/useCompanies'
import { useOrganizationsLive, useRefreshOrganizationsFromKaseya, type OrganizationLive } from '../../hooks/api/useOrganizationsLive'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { LoadingState } from '../../components/ui/LoadingState'
import { ErrorState } from '../../components/ui/ErrorState'
import { OrganizationDetailPanel } from '../../components/admin/OrganizationDetailPanel'
import { Select } from '../../components/ui/Select'
import { formatDistanceToNow } from 'date-fns'

const subscriptionOptions = [
  { label: 'All tiers', value: 'all' },
  { label: 'Essentials', value: 'essentials' },
  { label: 'Enhanced', value: 'enhanced' },
  { label: 'Premium', value: 'premium' }
] as const

type SubscriptionFilter = (typeof subscriptionOptions)[number]['value']

export const AdminUsersPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading: userLoading, error: userError } = useUserCheck(user?.id || '')
  const { data: organizations = [], isLoading: organizationsLoading, error: organizationsError } = useOrganizationsLive()
  const refreshMutation = useRefreshOrganizationsFromKaseya()
  const navigate = useNavigate()

  const [searchTerm, setSearchTerm] = useState('')
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilter>('all')
  const [selectedCompany, setSelectedCompany] = useState<OrganizationLive | null>(null)
  const [panelMode, setPanelMode] = useState<'overview' | 'edit' | 'permissions'>('overview')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const deleteCompanyMutation = useDeleteCompany()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    const handleClickAway = () => setOpenMenuId(null)
    document.addEventListener('click', handleClickAway)
    return () => document.removeEventListener('click', handleClickAway)
  }, [])

  const filteredOrganizations = useMemo(() => {
    return organizations.filter((org: OrganizationLive) => {
      const matchesSearch =
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (org.slug ? org.slug.toLowerCase().includes(searchTerm.toLowerCase()) : false)

      const matchesSubscription =
        subscriptionFilter === 'all' || org.subscription_tier === subscriptionFilter

      return matchesSearch && matchesSubscription
    })
  }, [organizations, searchTerm, subscriptionFilter])

  const totalUsers = useMemo(
    () => filteredOrganizations.reduce((acc: number, org: OrganizationLive) => acc + org.user_count, 0),
    [filteredOrganizations]
  )
  const totalDevices = useMemo(
    () => filteredOrganizations.reduce((acc: number, org: OrganizationLive) => acc + org.device_count, 0),
    [filteredOrganizations]
  )
  const totalTickets = useMemo(
    () => filteredOrganizations.reduce((acc: number, org: OrganizationLive) => acc + org.ticket_count, 0),
    [filteredOrganizations]
  )

  if (userLoading || organizationsLoading) {
    return <LoadingState message="Loading organizations..." />
  }

  if (userError || !userRecord || organizationsError) {
    return <ErrorState title="Unable to load organizations." description="Please try refreshing the page." />
  }

  const handleView = (company: OrganizationLive, mode: 'overview' | 'edit' | 'permissions' = 'overview') => {
    if (mode === 'overview') {
      navigate(`/admin/organizations/${company.id}`)
      return
    }
    setSelectedCompany(company)
    setPanelMode(mode)
    setOpenMenuId(null)
  }

  const handleDelete = async (company: OrganizationLive) => {
    const confirmed = window.confirm(`Delete ${company.name}? This cannot be undone.`)
    if (!confirmed) return

    try {
      await deleteCompanyMutation.mutateAsync(company.id)
      if (selectedCompany?.id === company.id) {
        setSelectedCompany(null)
      }
    } catch (error) {
      console.error('Failed to delete organization', error)
      alert('Failed to delete organization. Please try again.')
    }
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Organizations</h1>
            <p className="mt-1 text-sm text-gray-500">
              Review every client company and keep an eye on user and device counts.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
              {refreshMutation.isPending ? 'Refreshing...' : 'Refresh from Kaseya'}
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="flex items-center justify-between rounded-2xl border border-green-200 bg-green-50 px-6 py-4 text-sm text-green-700">
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-600 hover:text-green-800"
            >
              ✕
            </button>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Organizations</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{filteredOrganizations.length}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Users</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{totalUsers}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Devices</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{totalDevices}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Tickets</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{totalTickets}</p>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 items-center gap-3">
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
              <Select
                value={subscriptionFilter}
                onChange={(event) => setSubscriptionFilter(event.target.value as SubscriptionFilter)}
              >
                {subscriptionOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </div>
            <div className="text-sm text-gray-500">
              Showing {filteredOrganizations.length} of {organizations.length} organizations
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Kaseya Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subscription</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Devices</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tickets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Synced</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredOrganizations.map((org: OrganizationLive) => (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {org.logo_url ? (
                          <img src={org.logo_url} alt={org.name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-600">
                            {org.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{org.name}</p>
                          <p className="text-xs text-gray-500">{org.slug}</p>
                          {org.kaseya_live_name && org.kaseya_live_name !== org.name && (
                            <p className="text-xs text-orange-600">Kaseya: {org.kaseya_live_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {org.kaseya_synced ? (
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm">Synced</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-sm">Not Synced</span>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm capitalize text-gray-600">
                      {org.subscription_tier || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{org.user_count}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{org.device_count}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{org.ticket_count}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {org.last_synced_at ? formatDistanceToNow(new Date(org.last_synced_at)) + ' ago' : 'Never'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <div className="relative flex items-center justify-end gap-2">
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            handleView(org, 'overview')
                          }}
                          className="rounded-full border border-gray-300 p-2 text-gray-600 transition-colors hover:bg-gray-50"
                          aria-label="View organization"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpenMenuId(prev => (prev === org.id ? null : org.id))
                          }}
                          className="rounded-full border border-gray-300 p-2 text-gray-600 transition-colors hover:bg-gray-50"
                          aria-label="Organization actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {openMenuId === org.id && (
                          <div
                            className="absolute right-0 top-10 z-10 w-48 rounded-xl border border-gray-200 bg-white shadow-lg"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              onClick={() => handleView(org, 'edit')}
                              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Edit Organization
                            </button>
                            <button
                              onClick={() => handleView(org, 'permissions')}
                              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Manage Permissions
                            </button>
                            <button
                              onClick={() => handleDelete(org)}
                              className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                            >
                              Delete Organization
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredOrganizations.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                      No organizations match the search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <OrganizationDetailPanel
          company={selectedCompany}
          isOpen={Boolean(selectedCompany)}
          onClose={() => {
            setSelectedCompany(null)
            setPanelMode('overview')
          }}
          currentAdminId={userRecord.id}
          mode={panelMode}
          onModeChange={setPanelMode}
        />
      </div>
    </DashboardLayout>
  )
}
